import {
  BulbOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  BgColorsOutlined,
  ReloadOutlined,
  SwapOutlined,
  MailOutlined,
  LogoutOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Layout, Menu, Space, Switch, Tag, Typography } from "antd";
import type { MenuProps } from "antd";
import type { CSSProperties, PropsWithChildren } from "react";
import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { accountingNav, masterRecordNav } from "./navigation";
import { useAuthStore } from "../../stores/authStore";
import { useTenantStore } from "../../stores/tenantStore";
import { useThemeStore } from "../../stores/themeStore";
import { TenantYearSwitcher } from "../../shared/components/TenantYearSwitcher";
import { EmailComposeModal } from "../../shared/components/EmailComposeModal";
import { getAccountingPalette } from "../theme/accountingTheme";
import { hasPermission } from "../../shared/utils/permissions";

const { Header, Sider, Content } = Layout;
const accountingLogoSrc = "/assets/accounting-logo.svg";

function flattenKeys(items = accountingNav): string[] {
  return items.flatMap((item) => item.children?.length ? flattenKeys(item.children) : [item.key]);
}

export function AccountingShell({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const location = useLocation();
  const { navMode, collapsed, setCollapsed, toggleMode, setNavMode, setAccent, mode, accent } = useThemeStore();
  const { user, clearSession } = useAuthStore();
  const tenant = useTenantStore();
  const [tenantOpen, setTenantOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [openMenuKeys, setOpenMenuKeys] = useState<string[]>(["entry", "bill-entry", "reports"]);
  const siderScrollRef = useRef<HTMLDivElement>(null);

  const routeKeys = flattenKeys();
  const selectedKey = [...routeKeys]
    .sort((a, b) => b.length - a.length)
    .find((key) => key === "/" ? location.pathname === "/" : location.pathname === key || location.pathname.startsWith(`${key}/`)) ?? "/";

  const menuItems = useMemo<MenuProps["items"]>(
    () => accountingNav
      .map((item) => {
        const children = item.children
          ?.filter((child) => hasPermission(child.permission))
          .map((child) => ({ key: child.key, icon: child.icon, label: child.label }));
        if (item.children?.length && !children?.length) return null;
        if (!item.children?.length && !hasPermission(item.permission)) return null;
        return {
          key: item.key,
          icon: item.icon,
          label: item.label,
          children
        };
      })
      .filter(Boolean) as MenuProps["items"],
    [user]
  );

  const topbar = navMode === "topbar";
  const userName = user?.displayName || user?.email || "Accounting User";

  const profileMenuItems = useMemo<MenuProps["items"]>(() => {
    const masterItems = masterRecordNav
      .filter((item) => hasPermission(item.permission))
      .map((item) => ({ key: item.key, icon: item.icon, label: item.label }));

    return [
      { key: "profile", label: userName, icon: <UserOutlined />, disabled: true },
      { key: "role", label: <Tag>Admin / Accountant</Tag>, disabled: true },
      { type: "divider" },
      { key: "masters", label: "Master Records", children: masterItems },
      { type: "divider" },
      { key: "logout", label: "Logout", icon: <LogoutOutlined />, danger: true }
    ];
  }, [userName, user]);

  const palette = getAccountingPalette(accent, mode);
  const cssVars = useMemo(
    () => ({
      "--app-bg": palette.appBackground,
      "--header-bg": palette.headerBackground,
      "--header-text": palette.headerText,
      "--sider-bg": palette.siderBackground,
      "--sider-text": palette.siderText,
      "--primary": palette.primary,
      "--info": palette.info,
      "--success": palette.success,
      "--warning": palette.warning,
      "--error": palette.error
    }) as CSSProperties,
    [palette]
  );

  return (
    <div className={`accounting-app ${mode === "dark" ? "mode-dark" : "mode-light"}`} style={cssVars}>
      <Layout className="accounting-layout">
        {!topbar && (
          <Sider width={288} collapsedWidth={84} collapsed={collapsed} className="accounting-sider">
            <div className="brand-row">
              <img className="brand-logo" src={accountingLogoSrc} alt="Accounting logo" />
              {!collapsed && (
                <div>
                  <Typography.Title level={5} className="brand-title">Accounting</Typography.Title>
                </div>
              )}
            </div>
            <div className="accounting-sider-scroll" ref={siderScrollRef}>
              <Menu
                mode="inline"
                className="accounting-menu"
                selectedKeys={[selectedKey]}
                openKeys={collapsed ? undefined : openMenuKeys}
                inlineCollapsed={collapsed}
                items={menuItems}
                onOpenChange={(keys) => {
                  setOpenMenuKeys(keys.map(String));
                  requestAnimationFrame(() => {
                    const scrollBox = siderScrollRef.current;
                    const openItems = scrollBox?.querySelectorAll(".ant-menu-submenu-open");
                    const lastOpen = openItems?.[openItems.length - 1] as HTMLElement | undefined;
                    const title = lastOpen?.querySelector(".ant-menu-submenu-title") as HTMLElement | null;
                    const target = title ?? lastOpen;
                    target?.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
                    target?.focus?.({ preventScroll: true });
                  });
                }}
                onClick={({ key }) => { if (String(key).startsWith("/")) navigate(String(key)); }}
              />
            </div>
          </Sider>
        )}
        <Layout>
          <Header className={`accounting-header ${topbar ? "topbar-mode" : ""}`}>
            <div className="header-left">
              {!topbar && (
                <Button
                  type="text"
                  className="header-icon"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                />
              )}
              {topbar && <div className="brand-inline"><img className="brand-logo small" src={accountingLogoSrc} alt="Accounting logo" /><strong>Accounting</strong></div>}
              {topbar && (
                <Menu
                  mode="horizontal"
                  className="topbar-menu"
                  selectedKeys={[selectedKey]}
                  items={menuItems}
                  onClick={({ key }) => { if (String(key).startsWith("/")) navigate(String(key)); }}
                />
              )}
            </div>
            <div className="header-right">
              <Button className="context-switch-button" icon={<SwapOutlined />} onClick={() => setTenantOpen(true)}>
                {tenant.orgName ? `${tenant.orgName} / ${tenant.financialYear ?? "FY"}` : "Company / FY"}
              </Button>
              <Button className="header-icon" icon={<MailOutlined />} onClick={() => setEmailOpen(true)} />
              <Button className="header-icon" icon={mode === "dark" ? <BulbOutlined /> : <MoonOutlined />} onClick={toggleMode} />
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    { key: "sidebar", label: <Space>Top Bar Menu <Switch size="small" checked={topbar} /></Space> },
                    { type: "divider" },
                    { key: "indigo", label: "Indigo Theme" },
                    { key: "ocean", label: "Ocean Theme" },
                    { key: "forest", label: "Forest Theme" },
                    { key: "rose", label: "Rose Theme" },
                    { type: "divider" },
                    { key: "logout", label: "Sign Out" }
                  ],
                  onClick: ({ key }) => {
                    if (key === "sidebar") setNavMode(topbar ? "sidebar" : "topbar");
                    if (["indigo", "ocean", "forest", "rose"].includes(String(key))) setAccent(key as any);
                    if (key === "logout") { clearSession(); navigate("/login"); }
                  }
                }}
              >
                <Button className="header-icon" icon={<BgColorsOutlined />} />
              </Dropdown>
              <Button className="header-icon" icon={<ReloadOutlined />} onClick={() => window.location.reload()} />
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: profileMenuItems,
                  onClick: ({ key }) => {
                    if (key === "logout") {
                      clearSession();
                      navigate("/login");
                      return;
                    }
                    if (String(key).startsWith("/")) navigate(String(key));
                  }
                }}
              >
                <Button type="text" className="profile-button">
                  <Avatar icon={<UserOutlined />} />
                  <span>{userName}</span>
                </Button>
              </Dropdown>
            </div>
          </Header>
          <Content className="accounting-content">{children}</Content>
        </Layout>
      </Layout>
      <TenantYearSwitcher open={tenantOpen || !tenant.orgId || !tenant.financialYear} mandatory={!tenant.orgId || !tenant.financialYear} onClose={() => setTenantOpen(false)} />
      <EmailComposeModal open={emailOpen} onClose={() => setEmailOpen(false)} />
    </div>
  );
}
